import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { API, ORG, CONTACT_REASONS, LOGO_MARK } from "../lib/site";

const initialForm = {
    name: "",
    organization: "",
    email: "",
    phone: "",
    country: "",
    reason: "",
    message: "",
};

const Field = ({ id, testid, label, required, optional, error, children }) => (
    <div>
        <label htmlFor={id} className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#1A3B2B]/70">
            {label} {required ? "*" : optional ? <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span> : null}
        </label>
        {children}
        {error && (
            <p className="mt-2 text-xs font-semibold text-red-800" role="alert">
                {error}
            </p>
        )}
    </div>
);

const Contact = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your name.";
        if (form.organization.trim().length < 2) er.organization = "Please enter your organization.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
        if (form.country.trim().length < 2) er.country = "Please enter your country.";
        if (!form.reason) er.reason = "Please tell us how we can help.";
        if (form.message.trim().length < 10) er.message = "Please write a short message (at least 10 characters).";
        return er;
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("submitting");
        try {
            await axios.post(`${API}/contact`, form);
            setStatus("success");
            toast.success("Message received — thank you.");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong. Please try again or email us directly.");
        }
    };

    return (
        <main>
            <SEO
                title="Contact | Global Green Hotel"
                description="Contact Global Green Hotel in Waikoloa Village, Hawaiʻi — hotel partnerships, research, sponsorship, technology and general inquiries."
            />
            <PageHero
                testid="contact-hero"
                eyebrow="Contact"
                titleLines={["Start a conversation", "with GGH"]}
                description="Whether you are a hotel, researcher, sponsor or simply curious — we would like to hear from you."
            />

            <section data-testid="contact-section" className="py-24 sm:py-32">
                <div className="mx-auto grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
                    {/* Org details */}
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="grain relative overflow-hidden rounded-2xl bg-[#04150D] p-9 sm:p-11">
                                <div className="relative z-[2]">
                                    <img
                                        src={LOGO_MARK}
                                        alt="Global Green Hotel logo"
                                        className="w-44 drop-shadow-[0_0_26px_rgba(194,161,96,0.25)]"
                                        loading="lazy"
                                    />
                                    <h2 className="font-display mt-8 text-2xl font-medium tracking-tight text-[#FDFBF7]">
                                        GLOBAL GREEN HOTEL (GGH)
                                    </h2>
                                    <ul className="mt-8 space-y-6 text-sm text-[#FDFBF7]/75">
                                        <li className="flex gap-4">
                                            <MapPin size={18} className="mt-0.5 shrink-0 text-[#C2A160]" aria-hidden="true" />
                                            <span data-testid="contact-address">
                                                <span className="mb-1 block text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#C2A160]">
                                                    Location
                                                </span>
                                                {ORG.location}
                                            </span>
                                        </li>
                                        <li>
                                            <a href={`mailto:${ORG.email}`} data-testid="contact-email" className="flex items-center gap-4 transition-colors hover:text-[#D9B96A]">
                                                <Mail size={18} className="shrink-0 text-[#C2A160]" aria-hidden="true" />
                                                <span>
                                                    <span className="mb-1 block text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#C2A160]">
                                                        Email
                                                    </span>
                                                    {ORG.email}
                                                </span>
                                            </a>
                                        </li>
                                        <li>
                                            <a href={ORG.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 transition-colors hover:text-[#D9B96A]">
                                                <Phone size={18} className="shrink-0 text-[#C2A160]" aria-hidden="true" />
                                                <span>
                                                    <span className="mb-1 block text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#C2A160]">
                                                        Phone
                                                    </span>
                                                    {ORG.phone}
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            <Eyebrow>How Can We Help?</Eyebrow>
                            <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                                Send us a message
                            </h2>
                        </Reveal>

                        {status === "success" ? (
                            <Reveal delay={0.1}>
                                <div
                                    data-testid="contact-success"
                                    role="status"
                                    className="mt-10 rounded-2xl border border-[#1A3B2B]/20 bg-white p-10 shadow-[0_20px_60px_rgba(10,23,17,0.07)]"
                                >
                                    <CheckCircle2 size={36} className="text-[#1A3B2B]" aria-hidden="true" />
                                    <p className="mt-5 text-base leading-relaxed text-[#0A1711] md:text-lg">
                                        Thank you for contacting Global Green Hotel. We have
                                        received your message and will respond as soon as possible.
                                    </p>
                                    <button
                                        type="button"
                                        data-testid="contact-send-another"
                                        onClick={() => {
                                            setForm(initialForm);
                                            setStatus("idle");
                                        }}
                                        className="btn-outline-green mt-8"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            </Reveal>
                        ) : (
                            <Reveal delay={0.18}>
                                <form onSubmit={submit} noValidate data-testid="contact-form" className="mt-10 grid gap-5">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field id="ct-name" label="Name" required error={errors.name}>
                                            <input
                                                id="ct-name"
                                                data-testid="contact-name"
                                                type="text"
                                                value={form.name}
                                                onChange={set("name")}
                                                aria-invalid={!!errors.name}
                                                className={`input-field ${errors.name ? "input-error" : ""}`}
                                                placeholder="Your full name"
                                            />
                                        </Field>
                                        <Field id="ct-org" label="Organization" required error={errors.organization}>
                                            <input
                                                id="ct-org"
                                                data-testid="contact-organization"
                                                type="text"
                                                value={form.organization}
                                                onChange={set("organization")}
                                                aria-invalid={!!errors.organization}
                                                className={`input-field ${errors.organization ? "input-error" : ""}`}
                                                placeholder="Hotel, company, institution…"
                                            />
                                        </Field>
                                    </div>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field id="ct-email" label="Email" required error={errors.email}>
                                            <input
                                                id="ct-email"
                                                data-testid="contact-email-input"
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                aria-invalid={!!errors.email}
                                                className={`input-field ${errors.email ? "input-error" : ""}`}
                                                placeholder="you@example.com"
                                            />
                                        </Field>
                                        <Field id="ct-phone" label="Phone" optional>
                                            <input
                                                id="ct-phone"
                                                data-testid="contact-phone-input"
                                                type="tel"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                className="input-field"
                                                placeholder="+1 …"
                                            />
                                        </Field>
                                    </div>
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field id="ct-country" label="Country" required error={errors.country}>
                                            <input
                                                id="ct-country"
                                                data-testid="contact-country"
                                                type="text"
                                                value={form.country}
                                                onChange={set("country")}
                                                aria-invalid={!!errors.country}
                                                className={`input-field ${errors.country ? "input-error" : ""}`}
                                                placeholder="Country"
                                            />
                                        </Field>
                                        <Field id="ct-reason" label="How can we help?" required error={errors.reason}>
                                            <select
                                                id="ct-reason"
                                                data-testid="contact-reason"
                                                value={form.reason}
                                                onChange={set("reason")}
                                                aria-invalid={!!errors.reason}
                                                className={`input-field ${errors.reason ? "input-error" : ""}`}
                                            >
                                                <option value="">Select a reason…</option>
                                                {CONTACT_REASONS.map((r) => (
                                                    <option key={r} value={r}>{r}</option>
                                                ))}
                                            </select>
                                        </Field>
                                    </div>
                                    <Field id="ct-message" label="Message" required error={errors.message}>
                                        <textarea
                                            id="ct-message"
                                            data-testid="contact-message"
                                            rows={5}
                                            value={form.message}
                                            onChange={set("message")}
                                            aria-invalid={!!errors.message}
                                            className={`input-field resize-y ${errors.message ? "input-error" : ""}`}
                                            placeholder="How can GGH help you?"
                                        />
                                    </Field>
                                    <div>
                                        <button
                                            type="submit"
                                            data-testid="contact-submit"
                                            disabled={status === "submitting"}
                                            className="btn-green disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {status === "submitting" ? "Sending…" : "Send Message"}
                                        </button>
                                        <p className="mt-4 text-xs leading-relaxed text-[#4A5D54]">
                                            Your information is stored securely and is never
                                            published or shared publicly.
                                        </p>
                                    </div>
                                </form>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Contact;
